import {useCallback, useMemo} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {MultiSelectValue} from '../../api/columns/contracts/multi-select'
import type {PersistedOption} from '../../api/columns/contracts/single-select'
import type {SuggestedSingleSelectOption} from '../../helpers/suggestions'
import type {ColumnModelForDataType} from '../../models/column-model'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useUpdateItem} from '../use-update-item'

type UseMultiSelectEditorProps = {
  model: MemexItemModel
  columnModel: ColumnModelForDataType<typeof MemexColumnDataType.MultiSelect>
  selectedValueIds: Array<string>
  onSaved?: () => void
}

/**
 * Editor state for the multi-select cell.
 *
 * Mirrors `useSingleSelectEditor`, but `saveSelected` receives the full set of
 * currently-selected suggestion options (the SelectPanel commits the full
 * selection on close in multi-select mode) and forwards them as a
 * `MultiSelectValue` (`{ options: [{ id }, ...] }`) to the update pipeline.
 */
export function useMultiSelectEditor({model, columnModel, selectedValueIds, onSaved}: UseMultiSelectEditorProps) {
  const {updateItem} = useUpdateItem()

  const saveSelected = useCallback(
    async (nextSelected: Array<SuggestedSingleSelectOption>) => {
      const value: MultiSelectValue | undefined =
        nextSelected.length > 0 ? {options: nextSelected.map(o => ({id: o.id}))} : undefined

      await updateItem(model, {
        memexProjectColumnId: columnModel.id,
        dataType: MemexColumnDataType.MultiSelect,
        value,
        isIssueField: columnModel.isIssueField,
      })

      onSaved?.()
    },
    [columnModel.id, columnModel.isIssueField, model, onSaved, updateItem],
  )

  const options: Array<SuggestedSingleSelectOption> = useMemo(() => {
    const selectedIds = new Set(selectedValueIds)
    return columnModel.settings.options.map((option: PersistedOption) => ({
      ...option,
      selected: selectedIds.has(option.id),
    }))
  }, [columnModel.settings, selectedValueIds])

  const selected = useMemo(() => options.filter(o => o.selected), [options])

  return {saveSelected, options, selected}
}
